import { GetServerSideProps, GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import ErrorPage from 'next/error';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { EuropeanUnionList, apiQueryUserIpAndCountryInfo, openUrlWithNewWindow } from '@jove-frontend/uikit';
import { OpenInNewOutlined } from '@mui/icons-material';
import clsx from 'clsx';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import { getPostAndMorePosts } from '../../lib/contentful';

interface PostContent {
  json: any;
}

interface Post {
  title: string;
  content: PostContent;
}

interface PageProps {
  post: Post;
}

const INCLUDED_PAGES = [
  'privacy-policy',
  'terms-of-business-agreement',
  'privacy-policy-us',
  'terms-of-business-agreement-us',
  'privacy-policy-eu',
  'terms-of-business-agreement-eu',
];

const Page: React.FC<PageProps> = ({ post }) => {
  const router = useRouter();
  const { asPath, query } = router;
  const { slug } = query as { slug: string };
  const filteredPath = filterUrl(asPath);
  const hasVariation = INCLUDED_PAGES.includes(slug);
  const [pdfFileUrl, setPdfFileUrl] = useState(null);

  /**
   * effects
   */
  useEffect(() => {
    setPdfFileUrl(
      {
        'terms-of-business-agreement': '/TOBA_UK_v_2_8.pdf',
        'terms-of-business-agreement-us': '/TOBA_US_v_2_0.pdf',
        'terms-of-business-agreement-eu': '/TOBA_EU_v_1_5.pdf',
      }[slug]
    );
  }, [slug]);

  function filterUrl(url: string) {
    return url.replace(/-eu\?noRedirect=true$|-us\?noRedirect=true$|\?noRedirect=true$|(-eu$|-us$)/, '');
  }

  if (!router.isFallback && !post) {
    return <ErrorPage statusCode={404} />;
  }

  return (
    <div className="mx-auto flex w-full flex-col">
      <Header />
      <div className="h-0 flex-1">
        {router.isFallback ? (
          <p>Loading…</p>
        ) : (
          <>
            <Head>
              <title>{post.title} | Jove Insurance</title>
            </Head>
            <div className={clsx('mx-auto my-10 grid max-w-7xl px-2', hasVariation ? 'grid-cols-[300px_minmax(900px,_1fr)_100px]' : '')}>
              {hasVariation && (
                <div className="pr-12 pt-2 text-sm">
                  Other available versions for this document include:
                  <ul className="list-disc pl-4">
                    <li className="pt-2">
                      <a className="underline hover:no-underline" href={`${filteredPath}?noRedirect=true`}>
                        United Kingdom
                      </a>
                    </li>
                    <li>
                      <a className="underline hover:no-underline" href={`${filteredPath}-eu?noRedirect=true`}>
                        European Union
                      </a>
                    </li>
                    <li>
                      <a className="underline hover:no-underline" href={`${filteredPath}-us?noRedirect=true`}>
                        United States
                      </a>
                    </li>
                  </ul>
                </div>
              )}

              {!!pdfFileUrl ? (
                <iframe id="toba-pdf" className="h-screen w-full" src={pdfFileUrl} title="TOBA"></iframe>
              ) : (
                <article className="prose md:prose-sm prose-a:text-brand-primary w-full max-w-full">
                  {documentToReactComponents(post.content.json)}
                </article>
              )}
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Page;

export const getServerSideProps: GetServerSideProps<PageProps> = async (
  context: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<PageProps>> => {
  const {
    params,
    preview = false,
    query: { noRedirect },
  } = context;
  const { slug } = params as { slug: string };

  const requestUserCountryInfo = async () => {
    const [res] = await apiQueryUserIpAndCountryInfo();
    let country = res?.country || 'Unkown';

    return country;
  };

  const country = (await requestUserCountryInfo()) || 'Unknown';
  const isEU = Boolean(EuropeanUnionList.includes(country));
  let newSlug: string = slug;

  if (noRedirect !== 'true' && !slug.endsWith('-eu') && !slug.endsWith('-us') && INCLUDED_PAGES.includes(slug)) {
    newSlug = isEU ? `${slug}-eu` : country === 'US' ? `${slug}-us` : slug;
  }

  const data = await getPostAndMorePosts(newSlug, preview);

  if (!data?.post) {
    return { notFound: true };
  }

  return {
    props: {
      post: data.post,
    },
  };
};
